import { useRef, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import SVG from 'react-inlinesvg';
import cx from 'classnames';
import calendarSvg from 'assets/icons/calendar.svg';
import chevronDown from 'assets/icons/arrow_light_down.svg';
import closeSvg from 'assets/icons/close.svg';
import { getInputBorderStyle, getInputStyle } from 'lib/BaseInput';
import DateTimePicker from 'lib/DatePicker/DateTimePicker/DateTimePicker';
import { InputSize } from 'components/common/types/Input.types';
import { Store } from 'components/common/types/Store.types';
import { formatDateNoUTC } from 'modules/I18N';
import { isValid } from 'date-fns';
import { Matcher } from 'react-day-picker';
import { DateTimePickerChangeType } from 'lib/DatePicker/DateTimePicker/DateTimePicker.types';
import { FilterUploadDateProps } from './FilterUploadDate.types';

interface DateTimeFilterInputProps {
  dataTestId: string;
  value?: Date;
  placeholder: string;
  onChange: (value: Date, type: DateTimePickerChangeType) => void;
  onClear: () => void;
  disabledDays?: Matcher[];
  isDisabled?: boolean;
  timezoneAbr?: string;
}

const DateTimeFilterInput = ({
  dataTestId,
  value,
  placeholder,
  onChange,
  onClear,
  disabledDays,
  isDisabled,
  timezoneAbr,
}: DateTimeFilterInputProps): React.ReactElement => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const isValidDate = value && isValid(value);
  const displayValue = isValidDate
    ? `${formatDateNoUTC(value)} ${value.toLocaleTimeString('en-US', { hour12: true, hour: '2-digit', minute: '2-digit' })}${timezoneAbr ? ` ${timezoneAbr}` : ''}`
    : '';

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleClickOutside = (e: MouseEvent): void => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  return (
    <div
      ref={containerRef}
      data-test-id={dataTestId}
      className={getInputBorderStyle({
        classNames: 'relative text-neutral-600 [&:hover_svg]:text-neutral-950',
        isDisabled,
      })}
    >
      <SVG src={calendarSvg} className="ml-3 mr-1 text-neutral-950-opacity-30 w-5 h-5 pointer-events-none" />
      <input
        className={cx(getInputStyle({ size: InputSize.MEDIUM }), 'pl-0 cursor-pointer')}
        readOnly
        placeholder={placeholder}
        value={displayValue}
        onClick={() => !isDisabled && setIsOpen(!isOpen)}
        disabled={isDisabled}
      />
      {isValidDate ? (
        <SVG
          src={closeSvg}
          className="mr-3 ml-1 text-neutral-950-opacity-30 w-5 h-5 cursor-pointer hover:text-essential-primary"
          onClick={() => {
            onClear();
            setIsOpen(false);
          }}
        />
      ) : (
        <SVG src={chevronDown} className="mr-3 ml-1 text-neutral-950-opacity-30 w-5 h-5 pointer-events-none" />
      )}
      {isOpen && (
        <div className="absolute left-0 top-full z-50">
          <div className="bg-neutral-50 shadow-md border border-neutral-300 rounded-xl overflow-hidden">
            <DateTimePicker
              value={value}
              onChange={(newValue, type) => {
                onChange(newValue, type);
              }}
              disabledDays={disabledDays}
              isOpen={isOpen}
            />
            {timezoneAbr && (
              <div className="flex justify-center -mt-3 pb-3">
                <span className="text-xs text-essential-tertiary">{timezoneAbr}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const FilterUploadDate = ({
  uploadFrom,
  uploadTo,
  onChange,
  isDisabled,
}: FilterUploadDateProps): React.ReactElement => {
  const timezoneAbr = useSelector((state: Store) => state.header.timezone.tzAbr);

  const handleFromChange = (value: Date): void => {
    onChange({
      startDate: value,
      endDate: uploadTo && value > uploadTo ? null : (uploadTo ?? null),
    });
  };

  const handleToChange = (value: Date): void => {
    onChange({ startDate: uploadFrom ?? null, endDate: value });
  };

  return (
    <>
      <div className="flex-grow flex-shrink-0 min-w-62.5 max-w-100">
        <DateTimeFilterInput
          dataTestId="upload-from-datetime-picker"
          value={uploadFrom}
          placeholder="Upload from"
          onChange={handleFromChange}
          onClear={() => onChange({ startDate: null, endDate: uploadTo ?? null })}
          disabledDays={uploadTo ? [{ after: uploadTo }] : []}
          isDisabled={isDisabled}
          timezoneAbr={timezoneAbr}
        />
      </div>
      <div className="flex-grow flex-shrink-0 min-w-62.5 max-w-100">
        <DateTimeFilterInput
          dataTestId="upload-to-datetime-picker"
          value={uploadTo}
          placeholder="Upload to"
          onChange={handleToChange}
          onClear={() => onChange({ startDate: uploadFrom ?? null, endDate: null })}
          disabledDays={uploadFrom ? [{ before: uploadFrom }] : []}
          isDisabled={isDisabled}
          timezoneAbr={timezoneAbr}
        />
      </div>
    </>
  );
};

export default FilterUploadDate;
